import axios, { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { careersErrorMessage } from './careersErrorMessage';
import { resolveBaseUrl } from '@/lib/apiBaseUrl';
import { clearCandidateSession, readCandidateSession } from './session';
import { candidateLoginPath } from './redirect';
import { jobSearchParams } from './jobSearch';
import { JOBS_FILTER_SEPARATOR } from '../constants';
import type { CandidateOAuthProvider, CvColor, CvTemplate, SupportedLanguage } from '@hr-outsourcing/shared';
import type {
  CandidateInvitation,
  CandidateOAuthOutcome,
  CandidateOAuthSignupInput,
  CandidateApplication,
  CandidateApplyInput,
  CandidateJobDetail,
  CandidateAccount,
  CandidateAccountUpdate,
  CandidateCvUploadResult,
  CandidateProfile,
  CandidateProfileUpdate,
  CandidateRegisterInput,
  CandidateProfileStrength,
  CandidateSession,
  CareersJobPage,
  CareersJobSearch,
  JobRecommendations,
  SavedJob,
} from '@/types/careers';
import type { SignupVerification } from '@/types/signup';

const client = axios.create({ baseURL: `${resolveBaseUrl()}/careers` });

client.interceptors.request.use((config) => {
  const session = readCandidateSession();
  if (session) config.headers.Authorization = `Bearer ${session.token}`;
  return config;
});

client.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // An expired or revoked session sends the candidate back to the login page.
    const isAuthCall = error.config?.url?.startsWith('/auth/');
    if (error.response?.status === 401 && !isAuthCall) {
      clearCandidateSession();
      window.location.assign(candidateLoginPath(window.location.pathname));
    }
    // A failed change (save, apply, withdraw…) is told as the server's reason, as on the HR side.
    // Not for reads (their pages show the error in place), the sign-in forms (they show it on the
    // form) or a call that tells its failure itself (`silentError`).
    const isRead = (error.config?.method ?? 'get').toLowerCase() === 'get';
    if (error.response?.status !== 401 && !isRead && !isAuthCall && !error.config?.silentError) {
      toast.error(careersErrorMessage(error));
    }
    return Promise.reject(error);
  }
);

/** The server's message key of a failed request (MESSAGES.*), if it sent one. */
export function apiErrorKey(error: unknown): string | null {
  const data = (error as AxiosError<{ error?: { message?: string } }>)?.response?.data;
  return data?.error?.message ?? null;
}

/** Where the browser goes to sign in with a provider (a full-page redirect, not an XHR). */
export const oauthStartUrl = (provider: CandidateOAuthProvider, next: string | null): string =>
  `${resolveBaseUrl()}/careers/auth/${provider}/start${next ? `?next=${encodeURIComponent(next)}` : ''}`;

export const careersApi = {
  async getOAuthProviders(): Promise<CandidateOAuthProvider[]> {
    return (await client.get('/auth/providers')).data.data.providers;
  },
  /**
   * Collects the outcome of a provider sign-in. The HttpOnly nonce cookie set when the sign-in
   * started has to go along, hence withCredentials.
   */
  async exchangeOAuthCode(code: string): Promise<CandidateOAuthOutcome> {
    return (await client.post('/auth/oauth/exchange', { code }, { withCredentials: true })).data.data;
  },
  async completeOAuthSignup(input: CandidateOAuthSignupInput): Promise<CandidateSession> {
    return (await client.post('/auth/oauth/complete', input)).data.data;
  },
  /** Sends the sign-up form; the account opens once the e-mailed code is confirmed. */
  async register(input: CandidateRegisterInput): Promise<SignupVerification> {
    return (await client.post('/auth/register', input)).data.data;
  },
  async resendRegisterCode(verificationId: string): Promise<SignupVerification> {
    return (await client.post('/auth/register/resend', { verificationId })).data.data;
  },
  async verifyRegister(verificationId: string, code: string): Promise<CandidateSession> {
    return (await client.post('/auth/register/verify', { verificationId, code })).data.data;
  },
  async login(email: string, password: string): Promise<CandidateSession> {
    return (await client.post('/auth/login', { email, password })).data.data;
  },
  /** E-mails a reset link if the address has a candidate account (the answer is the same either way). */
  async forgotPassword(email: string): Promise<void> {
    await client.post('/auth/forgot-password', { email });
  },
  async validateResetToken(token: string): Promise<void> {
    await client.post('/auth/reset-password/validate', { token });
  },
  async resetPassword(token: string, password: string): Promise<void> {
    await client.post('/auth/reset-password', { token, password });
  },
  async logout(): Promise<void> {
    await client.post('/auth/logout');
  },
  async getAccount(): Promise<CandidateAccount> {
    return (await client.get('/me')).data.data.account;
  },
  async updateAccount(update: CandidateAccountUpdate): Promise<CandidateAccount> {
    return (await client.patch('/me', update)).data.data.account;
  },
  /** Done on its own when the site's language changes: nothing to tell the candidate if it fails. */
  async setLanguage(language: SupportedLanguage): Promise<CandidateAccount> {
    return (await client.patch('/me', { language }, { silentError: true })).data.data.account;
  },
  /** Sends a code to the new address; the account moves to it once the code is confirmed. */
  async startEmailChange(email: string): Promise<SignupVerification> {
    return (await client.post('/me/email', { email })).data.data;
  },
  async resendEmailChangeCode(verificationId: string): Promise<SignupVerification> {
    return (await client.post('/me/email/resend', { verificationId })).data.data;
  },
  /** Confirms the code; the session of the old address ends, the one handed back carries on. */
  async verifyEmailChange(verificationId: string, code: string): Promise<CandidateSession> {
    return (await client.post('/me/email/verify', { verificationId, code })).data.data;
  },
  async deleteAccount(): Promise<void> {
    await client.delete('/me');
  },
  async getProfile(): Promise<CandidateProfile> {
    return (await client.get('/profile')).data.data.profile;
  },
  async updateProfile(update: CandidateProfileUpdate): Promise<CandidateProfile> {
    return (await client.put('/profile', update)).data.data.profile;
  },
  async uploadCv(file: File): Promise<CandidateCvUploadResult> {
    const form = new FormData();
    form.append('cv', file);
    return (await client.post('/profile/cv', form)).data.data;
  },
  async getCvUrl(): Promise<string> {
    return (await client.get('/profile/cv')).data.data.url;
  },
  async deleteCv(): Promise<CandidateProfile> {
    return (await client.delete('/profile/cv')).data.data.profile;
  },
  /** The profile (as edited, not necessarily saved) laid out in a template and color, in a language, as a PDF. */
  async previewCv(
    design: { template: CvTemplate; color: CvColor | null; language: SupportedLanguage },
    profile: CandidateProfileUpdate
  ): Promise<Blob> {
    return (await client.post('/profile/cv/preview', { ...design, profile }, { responseType: 'blob', silentError: true })).data;
  },
  async uploadPhoto(file: File): Promise<CandidateProfile> {
    const form = new FormData();
    form.append('photo', file);
    return (await client.post('/profile/photo', form)).data.data.profile;
  },
  async deletePhoto(): Promise<CandidateProfile> {
    return (await client.delete('/profile/photo')).data.data.profile;
  },
  async getProfileStrength(): Promise<CandidateProfileStrength> {
    return (await client.get('/profile/strength')).data.data.strength;
  },
  async getRecommendations(): Promise<JobRecommendations> {
    return (await client.get('/recommendations')).data.data;
  },
  // The listings page's matches: the ones applied to as well, which it marks.
  // `countries`: the country filter (none: the candidate's own country).
  async getRecommendationsWithApplied(countries: string[] = []): Promise<JobRecommendations> {
    const country = countries.length > 0 ? countries.join(JOBS_FILTER_SEPARATOR) : undefined;
    return (await client.get('/recommendations', { params: { includeApplied: 'true', country } })).data.data;
  },
  async getSavedJobs(): Promise<SavedJob[]> {
    return (await client.get('/saved-jobs')).data.data.items;
  },
  async saveJob(slug: string): Promise<void> {
    await client.put(`/saved-jobs/${encodeURIComponent(slug)}`);
  },
  async unsaveJob(slug: string): Promise<void> {
    await client.delete(`/saved-jobs/${encodeURIComponent(slug)}`);
  },
  async getJobs(search: CareersJobSearch): Promise<CareersJobPage> {
    return (await client.get('/jobs', { params: jobSearchParams(search) })).data.data;
  },
  async getJob(slug: string): Promise<CandidateJobDetail> {
    return (await client.get(`/jobs/${encodeURIComponent(slug)}`)).data.data;
  },
  async apply(slug: string, input: CandidateApplyInput): Promise<CandidateApplication> {
    return (await client.post(`/jobs/${encodeURIComponent(slug)}/apply`, input)).data.data.application;
  },
  async getApplications(): Promise<CandidateApplication[]> {
    return (await client.get('/applications')).data.data.items;
  },
  /** The candidate opened the application: its "updated" badge goes. Fails quietly, it is only a badge. */
  async markApplicationSeen(id: string): Promise<void> {
    await client.post(`/applications/${encodeURIComponent(id)}/seen`, undefined, { silentError: true });
  },
  async withdrawApplication(id: string): Promise<void> {
    await client.post(`/applications/${encodeURIComponent(id)}/withdraw`);
  },
  async getInvitations(): Promise<CandidateInvitation[]> {
    return (await client.get('/invitations')).data.data.items;
  },
  async declineInvitation(id: string): Promise<void> {
    await client.post(`/invitations/${encodeURIComponent(id)}/decline`);
  },
};
